import { useEffect, useRef, useState } from "react";

// pdf.js (via unpdf's bundled build) is ~1.6 MB, so it is loaded only when a PDF is opened.
// We render one page at a time to a canvas instead of relying on the browser's built-in
// viewer, which is unreliable on mobile (iOS Safari often shows only the first page).
type PdfDocument = Awaited<ReturnType<typeof import("unpdf")["getDocumentProxy"]>>;

export interface PdfPreviewLabels {
  loading: string;
  unavailable: string;
  page: (page: number, total: number) => string;
  prev: string;
  next: string;
}

export function PdfPreview({ data, title, labels }: { data: ArrayBuffer; title: string; labels: PdfPreviewLabels }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [doc, setDoc] = useState<PdfDocument | null>(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    let loaded: PdfDocument | null = null;
    setStatus("loading");
    setPage(1);
    import("unpdf")
      .then(({ getDocumentProxy }) => getDocumentProxy(new Uint8Array(data.slice(0))))
      .then((pdf) => {
        loaded = pdf;
        if (cancelled) return void pdf.destroy();
        setDoc(pdf);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
      void loaded?.destroy();
    };
  }, [data]);

  useEffect(() => {
    if (!doc || !canvasRef.current) return;
    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<unknown> } | null = null;
    doc.getPage(page).then((pdfPage) => {
      const canvas = canvasRef.current;
      if (cancelled || !canvas) return;
      const cssWidth = Math.max(240, (wrapRef.current?.clientWidth ?? 800) - 2);
      const base = pdfPage.getViewport({ scale: 1 });
      const scale = cssWidth / base.width;
      const ratio = Math.min(window.devicePixelRatio || 1, 3);
      const viewport = pdfPage.getViewport({ scale: scale * ratio });
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
      canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;
      const context = canvas.getContext("2d");
      if (!context) return setStatus("error");
      task = pdfPage.render({ canvasContext: context, viewport, canvas } as Parameters<typeof pdfPage.render>[0]);
      task.promise.catch((error: { name?: string }) => {
        if (!cancelled && error?.name !== "RenderingCancelledException") setStatus("error");
      });
    }).catch(() => {
      if (!cancelled) setStatus("error");
    });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, page]);

  if (status === "error") return <div className="file-preview-status">{labels.unavailable}</div>;
  const total = doc?.numPages ?? 0;
  return (
    <div className="file-preview-pdf" ref={wrapRef}>
      {status === "loading" ? <div className="file-preview-status">{labels.loading}</div> : null}
      <canvas ref={canvasRef} aria-label={title} hidden={status !== "ready"} />
      {status === "ready" && total > 1 ? (
        <nav className="file-preview-pdf-pager">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>{labels.prev}</button>
          <span aria-live="polite">{labels.page(page, total)}</span>
          <button type="button" disabled={page >= total} onClick={() => setPage((p) => Math.min(total, p + 1))}>{labels.next}</button>
        </nav>
      ) : null}
    </div>
  );
}
